import { BrandImage } from '@components/Brand/BrandImage';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { BrandImageCard } from '@components/Home/BrandImageCard';
import { CustomerHeaderNav } from '@components/Layouts/CustomerHeaderNav';
import { RecallContentWrapper } from '@components/Layouts/RecallContentWrapper';
import { RouterLayout } from '@components/Layouts/RouterLayout';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingProposalRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiAsyncError } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { LoadingScreen, NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import { customerRoutes, hasErrorCode } from 'noddi-util';
import { clearProposalContext, registerProposalContext, trackProposalStarted } from 'noddi-web';
import { PropsWithChildren, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalBrandFromOrg } from '../hooks/useLocalBrandFromOrg';
import { useProposalUserGroupSync } from '../hooks/useProposalUserGroupSync';
import { getProposalAnalyticsProps } from '../utils/proposalAnalytics';
import { isProposalPayBeforeCheckoutInFlight } from '../utils/proposalPayBeforeCheckout';
import {
  BookingProposalContext,
  BookingProposalStore,
  createBookingProposalStore,
  type BookingProposalServerState,
  type ProposalRecipient
} from './bookingProposalStore';
import { useCurrentBrand } from './BrandStore/brandStore';
import { useRegisterFlowUserGroup } from './flowUserGroupStore';

/**
 * The proposal endpoint says "this one is over" with 404 (draft or unknown slug)
 * or 410 (expired, rejected, consumed, staff-closed). A transport failure has no
 * status at all and a 5xx is our own fault — neither means the customer's
 * proposal is gone, so neither may send them off to rebook what they still have.
 */
const isProposalGoneError = (error: NoddiAsyncError) => {
  const status = error.status ?? error.response?.status;

  return status === 404 || status === 410;
};

// Helper component to provide RouterLayout styling for error pages
const ErrorPageWrapper = ({ children }: { children: ReactNode }) => (
  <div className={cn('min-h-screen', 'bg-primary-background-gradient')}>
    <CustomerHeaderNav />
    {children}
  </div>
);

interface BookingProposalProps {
  bookingProposal: BookingProposalRecord;
}

const getProposalRecipient = ({ userGroupMember, organization }: BookingProposalRecord): ProposalRecipient => ({
  contactName: `${userGroupMember.user.firstName} ${userGroupMember.user.lastName}`,
  contactPhoneNumber: userGroupMember.user.phoneNumber || undefined,
  organizationName: organization ? userGroupMember.userGroup.name : undefined
});

const getProposalServerState = (bookingProposal: BookingProposalRecord): BookingProposalServerState => ({
  bookingProposalItems: bookingProposal.bookingProposalItems,
  slug: bookingProposal.slug,
  id: bookingProposal.id,
  type: bookingProposal.type.value,
  serviceDepartmentId: bookingProposal.serviceDepartment.id,
  userGroupId: bookingProposal.userGroupMember.userGroup.id,
  bookedForUserId: bookingProposal.userGroupMember.user.id,
  recipient: getProposalRecipient(bookingProposal),
  analytics: getProposalAnalyticsProps(bookingProposal)
});

function BookingProposalProvider({ children, ...props }: PropsWithChildren<BookingProposalProps>) {
  const storeRef = useRef<BookingProposalStore | undefined>(undefined);
  const { bookingProposal } = props;
  useProposalUserGroupSync({
    proposalId: bookingProposal.id,
    userGroupId: bookingProposal.userGroupMember.userGroup.id
  });
  useRegisterFlowUserGroup(bookingProposal.userGroupMember.userGroup.id);

  // eslint-disable-next-line react-hooks/refs -- lazy one-time store init in a ref during render; the zustand-context pattern
  if (!storeRef.current) {
    storeRef.current = createBookingProposalStore({
      address: props.bookingProposal.address ?? undefined,
      ...getProposalServerState(props.bookingProposal)
    });
  }

  useEffect(() => {
    storeRef.current?.getState().actions.syncProposal(getProposalServerState(props.bookingProposal));
  }, [props.bookingProposal]);

  // One owner for the whole flow: this wrapper is the only place that holds the
  // proposal on every step. Context goes out as super-props so the screen views
  // and the Mixpanel path events carry the same breakdown as the funnel events,
  // and is dropped on unmount so later booking-flow events are never tagged as
  // proposal traffic. `proposalStarted` is claimed once per proposal, so a deep
  // link into a later step still opens the funnel.
  const analytics = useMemo(() => getProposalAnalyticsProps(bookingProposal), [bookingProposal]);
  useEffect(() => {
    registerProposalContext(analytics);
    trackProposalStarted(analytics);
    return clearProposalContext;
  }, [analytics]);

  return (
    // eslint-disable-next-line react-hooks/refs -- store ref is filled earlier in this render, so reading it here is safe
    <BookingProposalContext.Provider value={storeRef.current}>
      <RouterLayout />
    </BookingProposalContext.Provider>
  );
}

export const BookingProposalWrapper = () => {
  const { slug } = useParams();
  const { currentBrand } = useCurrentBrand();

  const {
    data: bookingProposal,
    isPending: isBookingProposalPending,
    error: bookingProposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug: slug! }
  });

  useLocalBrandFromOrg({ serviceDepartmentId: bookingProposal?.serviceDepartment.id });

  if (isBookingProposalPending) {
    return <LoadingScreen />;
  }

  if (bookingProposalError) {
    if (hasErrorCode(bookingProposalError, 'booking_proposal_is_used_has_booking')) {
      // Pay-before creates the booking (closing this proposal) before the payment
      // session navigates away — keep a loader so we never flash "already booked".
      if (isProposalPayBeforeCheckoutInFlight()) {
        return <LoadingScreen />;
      }

      return (
        <ErrorPageWrapper>
          <RecallContentWrapper
            title={t`Booking confirmed`}
            subtitle={t`You already created a booking based on our suggestion`}
            hideBackButton
          >
            <NoddiBasicCard
              to={customerRoutes.bookings.getPath({})}
              className='flex flex-col gap-3 overflow-hidden p-0'
            >
              <BrandImage brand={currentBrand} />

              <div className='flex flex-col gap-3 px-5 pb-4'>
                {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
                <p>{t`Your booking is confirmed`} 💜</p>
                <NoddiDummyButton endIcon='ArrowRight' className='w-full'>
                  <Trans>See my bookings</Trans>
                </NoddiDummyButton>
              </div>
            </NoddiBasicCard>
          </RecallContentWrapper>
        </ErrorPageWrapper>
      );
    }

    if (isProposalGoneError(bookingProposalError)) {
      return (
        <ErrorPageWrapper>
          <RecallContentWrapper
            title={t`Hello again`}
            subtitle={t`Your suggested booking is no longer available, feel free to create a new booking`}
            hideBackButton
          >
            <BrandImageCard />
          </RecallContentWrapper>
        </ErrorPageWrapper>
      );
    }

    // Dead backend, offline, or a 5xx: the proposal is fine, the request was not.
    // Offer a retry instead of telling the customer their booking is gone.
    return (
      <ErrorPageWrapper>
        <ApiErrorPageWithTrans error={bookingProposalError} />
      </ErrorPageWrapper>
    );
  }

  return <BookingProposalProvider bookingProposal={bookingProposal} />;
};
